import { Link } from "react-router-dom";
import { ArrowRight, Phone, ShieldCheck, Award, Clock, CheckCircle2 } from "lucide-react";
import MicroSiteLayout from "@/components/microsite/MicroSiteLayout";
import QuoteForm from "@/components/shared/QuoteForm";
import { Button } from "@/components/ui/button";
import { COMPANY, PUBLIC_EMAIL } from "@/lib/constants";
import { useDomainBranding } from "@/hooks/useDomainBranding";
import { useMicroSiteContent } from "@/lib/micrositeContentLoader";

const MicroSiteHome = () => {
  const brand = useDomainBranding();
  const content = useMicroSiteContent(brand.brand)!;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${brand.canonicalOrigin}/#business`,
    name: brand.siteName,
    alternateName: COMPANY.name,
    parentOrganization: { "@type": "Organization", name: COMPANY.legalName, url: "https://obriencontractingusa.com" },
    description: brand.defaultDescription,
    url: brand.canonicalOrigin,
    telephone: COMPANY.phone,
    ...(PUBLIC_EMAIL ? { email: PUBLIC_EMAIL } : {}),
    image: brand.ogImage,
    address: {
      "@type": "PostalAddress",
      streetAddress: COMPANY.address.street,
      addressLocality: COMPANY.address.city,
      addressRegion: COMPANY.address.state,
      postalCode: COMPANY.address.zip,
      addressCountry: "US",
    },
    areaServed: { "@type": "State", name: "Florida" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: COMPANY.googleRating, reviewCount: COMPANY.googleReviews },
  };

  return (
    <MicroSiteLayout
      title={brand.defaultTitle}
      description={brand.defaultDescription}
      canonicalPath=""
      jsonLd={[localBusinessSchema]}
    >
      {/* Hero */}
      <section className="bg-hero py-16 md:py-24">
        <div className="container mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-primary-foreground">
            <p className="uppercase tracking-wider text-accent text-sm font-semibold mb-3">{brand.tagline}</p>
            <h1 className="text-3xl md:text-5xl font-heading font-bold mb-5 leading-tight">{content.heroH1}</h1>
            <p className="text-lg text-primary-foreground/85 mb-6 max-w-xl">{content.heroSubhead}</p>
            <ul className="space-y-2 mb-7">
              {content.heroBullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-primary-foreground/90">
                  <CheckCircle2 className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/quick-estimate">
                <Button variant="hero" size="lg">Get Free Estimate <ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
              <a href={COMPANY.phoneHref}>
                <Button variant="heroOutline" size="lg"><Phone className="mr-2 h-4 w-4" /> {COMPANY.phoneFormatted}</Button>
              </a>
            </div>
          </div>
          <div className="bg-background rounded-xl shadow-2xl p-6 md:p-8">
            <h2 className="text-xl font-heading font-bold text-foreground mb-2">Request your free quote</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Your request goes directly to O'Brien Contracting (FL CCC1335947).
            </p>
            <QuoteForm sourcePage={`${brand.brand}-home-hero`} variant="compact" />
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-secondary py-8 border-y border-border">
        <div className="container mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { Icon: ShieldCheck, label: "Licensed FL", sub: COMPANY.license },
            { Icon: Award, label: `${COMPANY.googleRating}★ Google`, sub: `${COMPANY.googleReviews}+ reviews` },
            { Icon: Clock, label: `${COMPANY.yearsExperience}+ years`, sub: "Roofing experience" },
            { Icon: CheckCircle2, label: "Lifetime", sub: "Workmanship guarantee" },
          ].map(({ Icon, label, sub }) => (
            <div key={label}>
              <Icon className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-sm font-semibold text-foreground">{label}</div>
              <div className="text-xs text-muted-foreground">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Services preview */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-heading font-bold text-foreground text-center mb-3">Our Services</h2>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-10">{content.servicesIntro}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {content.services.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="block p-6 rounded-xl border border-border bg-background hover:border-accent hover:shadow-lg transition-all"
              >
                <h3 className="font-heading font-bold text-lg text-foreground mb-2">{s.name}</h3>
                <p className="text-sm text-muted-foreground mb-3">{s.intro}</p>
                <span className="text-accent text-sm font-medium inline-flex items-center">
                  Learn more <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" size="lg">View all services <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-heading font-bold text-center text-foreground mb-8">Common Questions</h2>
          <div className="space-y-4">
            {content.faqs.slice(0, 3).map((f) => (
              <details key={f.q} className="bg-background rounded-lg p-5 border border-border group">
                <summary className="font-semibold text-foreground cursor-pointer flex justify-between items-center">
                  {f.q}
                  <span className="text-accent group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="text-accent font-medium inline-flex items-center">
              See all questions <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-hero">
        <div className="container mx-auto px-4 text-center text-primary-foreground">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Ready for a free quote?</h2>
          <p className="text-primary-foreground/85 mb-6 max-w-xl mx-auto">
            Call {COMPANY.phoneFormatted} or request your free estimate online — we respond within one business hour.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/quick-estimate"><Button variant="hero" size="lg">Get Free Estimate <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <a href={COMPANY.phoneHref}><Button variant="heroOutline" size="lg"><Phone className="mr-2 h-4 w-4" /> {COMPANY.phoneFormatted}</Button></a>
          </div>
        </div>
      </section>
    </MicroSiteLayout>
  );
};

export default MicroSiteHome;
